/**
 * Converts simple markdown text to HTML.
 * Supports: bold (**text**), headers (###), and list items (- or *)
 *
 * @param {string} markdown - The markdown string to convert
 * @param {Object} options - Optional styling configuration
 * @param {boolean} options.withStyles - Whether to include inline styles (default: true)
 * @returns {string} The converted HTML string
 */
export const formatMarkdownToHtml = (markdown, options = {}) => {
  const { withStyles = true } = options;

  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  // LLM-generated insight payloads sometimes contain literal "\n" (backslash + n)
  // instead of real newline characters. Normalize so headings/lists/paragraphs split correctly.
  const normalized = markdown.replace(/\\r\\n|\\n/g, '\n');
  const lines = normalized.split('\n');
  let html = '';
  let inList = false;

  lines.forEach((line) => {
    let text = line.trim();

    // Handle empty lines - close list if open
    if (!text) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      return;
    }

    // Inline formatting: Bold (**text**)
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Check for list items (- or *)
    const isListItem = text.startsWith('- ') || text.startsWith('* ');

    if (isListItem) {
      if (!inList) {
        html += withStyles ? '<ul style="padding-left: 20px;">' : '<ul>';
        inList = true;
      }
      const listContent = text.substring(2);
      html += withStyles
        ? `<li style="margin-bottom: 12px;">${listContent}</li>`
        : `<li>${listContent}</li>`;
    } else {
      // Close list if we were in one
      if (inList) {
        html += '</ul>';
        inList = false;
      }

      // Handle headers (###)
      if (text.startsWith('### ')) {
        const content = text.substring(4);
        html += withStyles
          ? `<p style="margin-top: 24px; margin-bottom: 8px;"><strong>${content}</strong></p>`
          : `<h3>${content}</h3>`;
      } else {
        // Standard paragraph
        html += withStyles
          ? `<p style="margin-bottom: 12px;">${text}</p>`
          : `<p>${text}</p>`;
      }
    }
  });

  // Close list if still open at end
  if (inList) {
    html += '</ul>';
  }

  return html;
};
